import { useForm } from "@mittwald/flow-components/dist/components/Form";
import { WizardStep } from "@mittwald/flow-components/dist/components/Wizard/components/WizardStep";
import type { WizardState } from "@mittwald/flow-components/dist/components/Wizard/state/model";
import type { AnimationController } from "@mittwald/flow-components/dist/hooks/useAnimationController";
import React from "react";
import { mittwaldApi } from "../../../../../../api/Mittwald";
import { useCodeInputAutoSubmit } from "../../../../../../hooks/useCodeInputAutoSubmit";
import type { VerifyMfaInputs } from "../../../../../../model/signup/Signup";
import User from "../../../../../../model/user/User";
import loginStore from "../../../../../../store/login";
import sessionStore from "../../../../../../store/session";
import { relocationWidgetStore } from "../../../store/RelocationWidgetStore";
import { LegacySystemAPI } from "../../../utils/LegacySystemAPI";
import { MfaStepContent } from "./MfaStepContent";

export interface MfaStepProps {
  isNewSystem: boolean;
  rejectionAnimation: AnimationController;
  email: string;
  password: string;
  wizardState: WizardState;
  onSuccessfulLogin?: (userId: string) => void;
}

export const MfaStep: React.FC<MfaStepProps> = (props) => {
  const {
    isNewSystem,
    rejectionAnimation,
    email,
    password,
    wizardState,
    onSuccessfulLogin,
  } = props;

  const mfaForm = useForm<VerifyMfaInputs>({
    onSubmit: async (values) => {
      if (isNewSystem) {
        const result = await mittwaldApi.userAuthenticateMfa.request(
          {
            requestBody: {
              multiFactorCode: values.multiFactorCode,
              email,
              password,
            },
          },
          {
            timeout: 30 * 1000,
          },
        );

        if (result.status !== 200) {
          rejectionAnimation.start();
          throw new Error(result.content.message);
        }

        relocationWidgetStore.updateAccessToken(result.content.token);
        sessionStore.setAuthentication(result.content);
        sessionStore.setMfaEnabled(true);
        loginStore.clearFirstFactorInformation();
        const user = await User.me();
        onSuccessfulLogin?.(user.id);
        wizardState.gotoStep("target");
      } else {
        const result = await LegacySystemAPI.login(
          email,
          password,
          values.multiFactorCode,
        );

        if (result.token) {
          relocationWidgetStore.updateAccessToken(result.token);
          wizardState.gotoStep("target");
        }
      }
    },
  });

  useCodeInputAutoSubmit(mfaForm);

  return (
    <WizardStep form={mfaForm} id="mfa" indicatorText="mfa" noReturn>
      <MfaStepContent rejectionAnimation={rejectionAnimation} />
    </WizardStep>
  );
};
